import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Divider,
  Paper,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Close,
  Info
} from '@mui/icons-material';
import TransactionRating from './TransactionRating';
import useApi from 'hooks/useApi';
import VerificationChip, { VerificationEntry, latestVerificationFor } from 'components/VerificationChip';

interface ResourceDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  resource: any;
  currentTransactionId?: string;
  allowRating?: boolean;
  userId?: string;
}

const ResourceDetailsDialog: React.FC<ResourceDetailsDialogProps> = ({
  open,
  onClose,
  resource,
  currentTransactionId,
  allowRating = false,
  userId
}) => {
  const { getResourceDataByUid, getSignedUrl } = useApi();
  const [isRatingAllowed, setIsRatingAllowed] = useState<boolean>(false);
  const [loadingRatingStatus, setLoadingRatingStatus] = useState<boolean>(false);
  const [imageSignedUrls, setImageSignedUrls] = useState<Record<string, string>>({});
  const [verifications, setVerifications] = useState<VerificationEntry[]>([]);

  useEffect(() => {
    if (!open || !resource?.fields) {
      setImageSignedUrls({});
      return;
    }

    let cancelled = false;
    const fetchSignedUrls = async () => {
      const entries = Object.entries(resource.fields).filter(
        ([, value]: [string, any]) =>
          value && typeof value === 'object' && !Array.isArray(value) && value.file_path && !value.signedUrl
      );

      const results = await Promise.all(
        entries.map(async ([key, value]: [string, any]) => {
          try {
            const res = await getSignedUrl({ file_path: value.file_path });
            const url = res?.data?.signed_url || res?.data?.signedUrl || res?.data?.url || null;
            return url ? [key, url] as const : null;
          } catch {
            return null;
          }
        })
      );

      if (cancelled) return;
      const next: Record<string, string> = {};
      results.forEach((entry) => {
        if (entry) next[entry[0]] = entry[1];
      });
      setImageSignedUrls(next);
    };

    fetchSignedUrls();
    return () => {
      cancelled = true;
    };
  }, [resource, open, getSignedUrl]);

  // Fetch resource type details to check if rating is allowed
  useEffect(() => {
    const fetchResourceTypeDetails = async () => {
      if (!resource || !open) {
        return;
      }

      // Try to get resource type ID from various possible locations
      const resourceTypeId = resource.id

      if (!resourceTypeId) {
        console.warn('No resource ID found for resource:', resource);
        setIsRatingAllowed(false);
        return;
      }

      try {
        setLoadingRatingStatus(true);
        const response = await getResourceDataByUid(resourceTypeId);
        
        if (response?.success && response?.data) {
          const allowRatingValue = response.data.resourceTypeId.allowRating ?? false;
          setIsRatingAllowed(allowRatingValue);
          setVerifications(Array.isArray(response.data.verifications) ? response.data.verifications : []);
        } else {
          setIsRatingAllowed(false);
          setVerifications([]);
        }
      } catch (error) {
        console.error('Error fetching resource type details:', error);
        setIsRatingAllowed(false);
      } finally {
        setLoadingRatingStatus(false);
      }
    };

    fetchResourceTypeDetails();
  }, [resource, open, getResourceDataByUid]);

  if (!resource) {
    return null;
  }

  // Extract resource display information
//   const getResourceDisplayName = () => {
//     if (!resource?.fields || typeof resource.fields !== 'object') {
//       return resource?.name || resource?.displayName || 'Unnamed Resource';
//     }
    
//     const fieldValues = Object.values(resource.fields);
//     return fieldValues.length > 0 ? String(fieldValues[0]) : 'Unnamed Resource';
//   };

  const getResourceDetails = () => {
    const details: Array<{ label: string; value: string | React.ReactNode }> = [];
    
    // Add basic info
    // details.push({
    //   label: 'Name',
    //   value: getResourceDisplayName()
    // });

    // Add custom fields if available
    if (resource.fields && typeof resource.fields === 'object') {
      Object.entries(resource.fields).forEach(([key, value], index) => {
        if (key.toLowerCase() !== 'team') { // Skip first field as it's used as display name and exclude team field
          const isImageField =
            value && typeof value === 'object' && !Array.isArray(value) &&
            ((value as any).public_url || (value as any).file_path);
          if (isImageField) {
            const imgVal = value as any;
            const src = imgVal.signedUrl || imageSignedUrls[key] || imgVal.public_url || '';
            const verification = latestVerificationFor(verifications, key);
            details.push({
              label: key,
              value: (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {src ? (
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: '#fff'
                      }}
                    >
                      <img
                        src={src}
                        alt={imgVal.file_name || key}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                      />
                    </Box>
                  ) : (
                    <span>{imgVal.file_name || 'Image'}</span>
                  )}
                  {verification && <Box><VerificationChip verification={verification} /></Box>}
                </Box>
              )
            });
          } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            // Skip unknown object values to avoid "[object Object]"
            return;
          } else {
            details.push({
              label: key,
              value: String(value)
            });
          }
        }
      });
    }

    if (resource.category || resource.resourceTypeId?.category) {
      details.push({
        label: 'Category',
        value: (
          <Chip 
            label={resource.category || resource.resourceTypeId?.category} 
            size="small" 
            color="primary" 
            variant="outlined"
          />
        )
      });
    }

    if (resource.resourceTypeId?.name) {
      details.push({
        label: 'Resource Type',
        value: resource.resourceTypeId.name
      });
    }

    if (resource.createdAt) {
      details.push({
        label: 'Created',
        value: new Date(resource.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      });
    }

    return details;
  };

  const resourceDetails = getResourceDetails();
  const resourceId = resource.id || resource._id;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Resource Details
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Resource Details Section */}
        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
            Resource Information
          </Typography>
          <Paper variant="outlined" sx={{ bgcolor: 'background.paper' }}>
            <Table size="small">
              <TableBody>
                {resourceDetails.map((detail, index) => (
                  <TableRow key={index} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell 
                      component="th" 
                      scope="row" 
                      sx={{ 
                        fontWeight: 500, 
                        color: 'text.secondary',
                        width: '35%',
                        py: 0.5,
                        px: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {detail.label}:
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        py: 0.5, 
                        px: 1,
                        fontSize: '0.875rem',
                        textTransform:"uppercase"
                      }}
                    >
                      {detail.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>

        <Divider />

        {/* Rating Section - Only show if resource type allows rating */}
        {resourceId && userId && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
              Resource Performance Ratings
            </Typography>
            {loadingRatingStatus ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Checking rating availability...
                </Typography>
              </Box>
            ) : isRatingAllowed ? (
              <TransactionRating
                resourceId={resourceId}
                userId={userId}
                currentTransactionId={currentTransactionId}
                allowRating={allowRating}
                compact={true}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                Rating is not enabled for this resource type.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ 
        p: 1, 
        borderTop: '1px solid', 
        borderColor: 'divider',
        bgcolor: 'grey.50'
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResourceDetailsDialog;
